/// <reference types='google.maps' />
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MapConfig } from 'libs/portal/src/models/MapConfig';
import { ToastrService } from 'ngx-toastr';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { CardTableAction, CardTableOptions } from 'libs/portal/src/modules/tools/card-table/models';
import { CardTableHeader } from 'libs/portal/src/modules/tools/card-table/models/card-table-header.model';
import { FilterItem, FilterOptions } from 'libs/portal/src/modules/tools/filters/models';
import { go } from 'fuzzysort';
import { DeviceLocation } from 'libs/portal/src/models/DeviceLocation';
import { CardTableComponent } from '../card-table/components';
import { ScreenSize } from 'libs/portal/src/layout/layout.service';
import { GetDeviceLocationRequest } from 'libs/portal/src/models/GetDeviceLocationRequest';

declare const MarkerClusterer: any;

@Component({
  selector: 'tools-device-map',
  templateUrl: './device-map.component.html',
  styleUrls: ['./device-map.component.scss'],
})
export class DeviceMapComponent implements OnInit, OnChanges {
  @ViewChild('mapContainer', { static: false }) gmap: ElementRef;
  @ViewChild('HopTable', { static: false }) public HopTable: CardTableComponent;

  @Input() config: MapConfig;
  @Input() locationData: DeviceLocation[];
  @Input() detailsObject: any;
  @Input() disableAdvanced = false;
  @Input() isMikrotik = false;

  @Output() searchChanged: EventEmitter<GetDeviceLocationRequest> = new EventEmitter<GetDeviceLocationRequest>();
  @Output() advancedToggled: EventEmitter<boolean> = new EventEmitter<boolean>();

  ScreenSize = ScreenSize;
  locations: any[];
  map: google.maps.Map;
  // staticMapURL: string;
  hopsOffset = 0;
  currentHopsPage = 1;
  showHops = false;
  hopsDesc = false;
  lat = -26.061351;
  lng = 28.153786;
  LocationTracingEnabled = false;
  CurrentTraceIndex = 0;
  TotalTraceIndex = 0;
  markers: google.maps.Marker[] = [];
  circles: google.maps.Circle[] = [];
  signalStrengths: google.maps.Circle[] = [];
  ringfenceCircles: google.maps.Circle[] = [];
  advancedLocation = false;
  dateChanged = false;
  tempStart = this.tools.GetStartOfMonth(new Date());
  tempEnd = this.tools.GetEndOfMonth();
  defaultStart = this.tools.GetStartOfMonth(new Date());
  defaultEnd = this.tools.GetEndOfMonth();
  advancedFilters = true;
  currentCenter = new google.maps.LatLng(this.lat, this.lng);
  disableMap = false; // Remove after testing
  showSignalStrengths = true;
  showRingfences = false;

  loadingHops = true;
  hopData: any = null;
  filteredHopData: any[] | null = null;
  hopsSearch = '';

  mapOptions: google.maps.MapOptions = {
    center: this.currentCenter,
    zoom: 6,
    scaleControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    minZoom: 4,
    zoomControl: true,
  };

  radiusCircleConfig = {
    strokeColor: null,
    strokeOpacity: 0.4,
    strokeWeight: 2,
    fillColor: null,
    fillOpacity: 0.3,
    map: null,
    center: null,
    radius: 0,
  };

  signalCircleConfig = {
    strokeColor: null,
    strokeOpacity: 0.3,
    strokeWeight: 1.5,
    fillColor: null,
    fillOpacity: 0.03,
    map: null,
    center: null,
    radius: 0,
  };

  markerClusterer: any;
  locationClusters: any[];
  jumps: any[];

  ringfenceGreenColor = '#7EDC52';
  signalStrengthColor = '#191970';

  formGroup: UntypedFormGroup = new UntypedFormGroup({
    MaxHopControl: new UntypedFormControl(5, Validators.compose([Validators.min(1), Validators.required, Validators.max(1000)])),
    LACSearchControl: new UntypedFormControl(null, [Validators.pattern(/^[0-9]*$/gm)]),
    CellIDSearchControl: new UntypedFormControl(null, [Validators.pattern(/^[0-9]*$/gm)]),
    MCCSearchControl: new UntypedFormControl(null, [Validators.pattern(/^[0-9]*$/gm)]),
    MNCSearchControl: new UntypedFormControl(null, [Validators.pattern(/^[0-9]*$/gm)]),
    DistinctTowerControl: new UntypedFormControl(true, []),
    FuzzySearchControl: new UntypedFormControl('', []),
    OperatorControl: new UntypedFormControl('', []),
  });

  hopsTableOptions: CardTableOptions = {
    titleProperty: 'name',
    subTitleProperty: 'status',
    additionalProperty: 'data',
    rowId: 'id',
    selectedProperty: 'selected',
    isSelectable: false,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'concat', 'id', 'lat', 'long', 'csq', 'rawmnc', 'range', 'radius'],
    stylePropertyPrefix: 'devices',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  mikrotikHopsTableOptions: CardTableOptions = {
    titleProperty: 'name',
    subTitleProperty: 'status',
    additionalProperty: 'data',
    rowId: 'id',
    selectedProperty: 'selected',
    isSelectable: false,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'concat', 'id', 'lat', 'long', 'csq', 'rawmnc', 'range', 'radius', 'totimestamp', 'textrange', 'expected'],
    stylePropertyPrefix: 'devices',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  hopRowActions: CardTableAction[] = [];

  hopFilterOptions: FilterOptions = {
    showSearch: true,
    searchPlaceholder: 'Search for hops between towers',
    showClearChip: true,
    enableChipsSortToggle: true,
    enableSearchFilterToggle: true,
  };

  hopFilterChips: FilterItem[] = [];

  headers: CardTableHeader[] = [
    {
      ID: 'label',
      Text: 'Index',
      Width: 5,
    },
    {
      ID: 'towerlabel',
      Text: 'Tower',
      Width: 5,
    },
    {
      ID: 'timestamp',
      Text: 'From',
      Width: 13,
    },
    {
      ID: 'totimestamp',
      Text: 'To',
      Width: 13,
    },
    {
      ID: 'cellid',
      Text: 'Cell ID',
      Width: 8,
    },
    {
      ID: 'lac',
      Text: 'LAC',
      Width: 6,
    },
    {
      ID: 'mcc',
      Text: 'MCC',
      Width: 6,
    },
    {
      ID: 'mnc',
      Text: 'Network',
      Width: 6,
    },
    {
      ID: 'textrange',
      Text: 'Est. Distance',
      Width: 11,
    },
    {
      ID: 'expected',
      Text: 'Status',
      Width: 12,
    },
    {
      ID: 'hopCount',
      Text: 'Ping Count',
      Width: 8,
    },
  ];

  mikrotikHeaders: CardTableHeader[] = [
    {
      ID: 'label',
      Text: 'Index',
      Width: 5,
    },
    {
      ID: 'towerlabel',
      Text: 'Tower',
      Width: 6,
    },
    {
      ID: 'timestamp',
      Text: 'From',
      Width: 17,
    },
    {
      ID: 'cellid',
      Text: 'Cell ID',
      Width: 10,
    },
    {
      ID: 'lac',
      Text: 'LAC',
      Width: 6,
    },
    {
      ID: 'mcc',
      Text: 'MCC',
      Width: 6,
    },
    {
      ID: 'mnc',
      Text: 'Network',
      Width: 10,
    },
  ];

  constructor(private tools: ToolsService, private toastService: ToastrService) { }

  ngOnInit() {
    this.drawHistoryLocations(this.locationData);

    // Custom scroll zoom logic
    const mapContainer = document.getElementById('map'); // Ensure this ID is correct
    if (mapContainer) {
      mapContainer.addEventListener('wheel', (event: WheelEvent) => this.tools.handleZoom(event, this.map));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // You can also use categoryId.previousValue and
    // categoryId.firstChange for comparing old and new values
    if (changes['config'] != null) {
      this.config = changes['config'].currentValue;
    }

    if (changes['locationData']) {
      this.locationData = changes['locationData'].currentValue;
      this.drawHistoryLocations(this.locationData);
    }
  }

  reCenter(data: any[]) {
    this.currentCenter = this.buildBounds(data).getCenter();
  }

  buildBounds(data: any[]): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    if (data != null) {
      data.forEach((element) => {
        if (element.Lat != null && element.Long != null && element.Lat != 0 && element.Long != 0) {
          bounds.extend(new google.maps.LatLng(element.Lat, element.Long));
        }
      });
    }
    return bounds;
  }

  drawMovementMarkers(data: any[]) {
    if (this.map == null) {
      return;
    }

    const infowindow = new google.maps.InfoWindow();
    const bounds: any = new google.maps.LatLngBounds();

    let count = 0;
    data.forEach((location: any, index: number) => {
      const center = new google.maps.LatLng(location.Lat, location.Long);
      if (location.expected != 'Unknown Tower') {
        let iconInfo: any = null;

        if (count == 1) {
          iconInfo = this.tools.GetBasicTowerMarkerInfo(location.tower.MNC, 'p');
        } else if (count == 0) {
          iconInfo = this.tools.GetBasicTowerMarkerInfo(location.tower.MNC, 'c');
        } else if (location.expected == true) {
          iconInfo = this.tools.GetBasicTowerMarkerInfo(location.tower.MNC, 'a');
        } else {
          iconInfo = this.tools.GetBasicTowerMarkerInfo(location.tower.MNC, null);
        }

        if (iconInfo != null) {
          iconInfo.icon = 'assets/map-markers/' + iconInfo.icon;
        }
        const marker = new google.maps.Marker({
          position: center,
          map: this.map,
          label: {
            text: location.label != null ? location.label.toString() : this.config.TerminalID,
            color: 'black',
            fontSize: '10pt',
            fontWeight: 'bold',
            className: 'map-marker',
          },
          icon: iconInfo.icon,
        });

        google.maps.event.addListener(
          marker,
          'click',
          ((marker, i) => {
            return () => {
              infowindow.setOptions({
                content:
                  `
                <style>
                  .gm-style-iw, .gm-style-iw-d{ height: 250px !important; max-height: 250px !important; }
                </style>
                <div id="content" style="overflow-y: scroll !important;">
                  <h1 id="firstHeading" class="firstHeading">Tower: ${location.label} ` +
                  (i == 0 ? `(Current Location)` : ``) +
                  `</h1>
                  <div>
                    <p>CellID: ${location.tower.CellID != null ? location.tower.CellID : '0'}</p>
                    <p>LAC: ${location.tower.LAC != null ? location.tower.LAC : '0'}</p>
                    <p>MCC: ${location.tower.MCC != null ? location.tower.MCC : 'N/A'}</p>
                    <p>Network: ${iconInfo.name}</p>
                    <p>Latitude: ${location.Lat}</p>
                    <p>Longitude: ${location.Long}</p>
                    <p><u>Distinct Connections: ${location.counter}</u></p>
                    ` +
                  this.getDeviceTowerPingList(location.Devices) +
                  `
                  </div>
                </div>`,
                maxWidth: 400,
                minWidth: 300,
              });
              infowindow.open(this.map, marker);
            };
          })(marker, index),
        );

        if (location.expected == true) {
          const radius = new google.maps.Circle({
            strokeColor: this.ringfenceGreenColor,
            strokeOpacity: this.radiusCircleConfig['strokeOpacity'],
            strokeWeight: this.radiusCircleConfig['strokeWeight'],
            fillColor: this.ringfenceGreenColor,
            fillOpacity: this.radiusCircleConfig['fillOpacity'],
            map: this.map,
            center: center,
            radius: location.Radius,
          });
          this.ringfenceCircles.push(radius);
        } else if (location.Ringfence != null && this.showRingfences == true) {
          const radius = new google.maps.Circle({
            strokeColor: this.ringfenceGreenColor,
            strokeOpacity: this.radiusCircleConfig['strokeOpacity'],
            strokeWeight: this.radiusCircleConfig['strokeWeight'],
            fillColor: this.ringfenceGreenColor,
            fillOpacity: this.radiusCircleConfig['fillOpacity'],
            map: this.map,
            center: center,
            radius: location.Ringfence,
          });
          this.ringfenceCircles.push(radius);
        }

        if (this.showSignalStrengths) {
          const signalStrength = new google.maps.Circle({
            strokeColor: this.signalStrengthColor,
            strokeOpacity: this.signalCircleConfig['strokeOpacity'],
            strokeWeight: this.signalCircleConfig['strokeWeight'],
            fillColor: this.signalStrengthColor,
            fillOpacity: this.signalCircleConfig['fillOpacity'],
            map: this.map,
            center: center,
            radius: this.tools.CalculateRadiusDistanceBasedOnCSQ(location.CSQ, location.Radius),
          });
          this.signalStrengths.push(signalStrength);
        }

        count++;
        bounds.extend(marker.getPosition());
        this.markers.push(marker);
      }
    });

    const padding = 0.01; // Adjust this value based on how much you want to expand
    const southWest = new google.maps.LatLng(bounds.getSouthWest().lat() - padding, bounds.getSouthWest().lng() - padding);
    const northEast = new google.maps.LatLng(bounds.getNorthEast().lat() + padding, bounds.getNorthEast().lng() + padding);
    bounds.extend(southWest);
    bounds.extend(northEast);

    setTimeout(() => {
      this.map.fitBounds(bounds);
      this.map.panToBounds(bounds);

    });

    setTimeout(() => {
      google.maps.event.trigger(this.map, 'resize');
    });
  }

  clearMarkers() {
    if (this.markers == null) {
      return;
    }
    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].setMap(null);
    }
    this.markers = [];
  }

  clearCircles() {
    if (this.circles == null) {
      return;
    }
    for (let i = 0; i < this.circles.length; i++) {
      this.circles[i].setMap(null);
      this.circles[i].setRadius(0);
    }
    this.circles = [];
  }

  clearRingfenceCircles() {
    if (this.ringfenceCircles == null) {
      return;
    }
    for (let i = 0; i < this.ringfenceCircles.length; i++) {
      this.ringfenceCircles[i].setMap(null);
      this.ringfenceCircles[i].setRadius(0);
    }
    this.ringfenceCircles = [];
  }

  clearSignalStrengths() {
    if (this.signalStrengths == null) {
      return;
    }
    for (let i = 0; i < this.signalStrengths.length; i++) {
      this.signalStrengths[i].setMap(null);
      this.signalStrengths[i].setRadius(0);
    }
    this.signalStrengths = [];
  }

  initialiseMap(callback: Function, overrideCenterChecks = false) {
    if (this.disableMap || (!overrideCenterChecks && (Number.isNaN(this.lat) || this.lat == 0 || Number.isNaN(this.lng) || this.lng == 0))) {
      return;
    }

    if (this.locationClusters?.length > 0) {
      this.reCenter(this.locationClusters);
    } else {
      this.currentCenter = new google.maps.LatLng(this.lat, this.lng);
    }

    this.mapOptions = {
      center: this.currentCenter,
      scaleControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      minZoom: 4,
      zoom: 10,
      zoomControl: true,
    };
    if (this.gmap) {
      // **** START
      //TODO: Fix this patch to properly redraw the map canvas.
      // Current issue is that the map does not reload properly after markers are changed
      // this.map = null;
      // setTimeout( () => {
      //   this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);
      // });
      if (!this.map) {
        this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);
      } else {
        this.map.unbindAll();
      }

      // **** END
    }

    if (callback != null) {
      setTimeout(() => {
        callback();
      });
    }
  }

  startDateInput(event) {
    this.tempStart = event.value;
  }

  endDateInput(event) {
    this.tempEnd = event.value;
  }

  clearPicker() {
    this.tempStart = this.defaultStart;
    this.tempEnd = this.defaultEnd;
  }

  dateChange(event) {
    if (!event) {
      this.defaultStart = this.tempStart;
      this.defaultEnd = this.tempEnd;
    }
  }

  toggleDistinctTowers() {
    this.formGroup.get('DistinctTowerControl')?.setValue(!this.formGroup.get('DistinctTowerControl')?.value);
    this.drawHistoryLocations(this.locations);
  }

  toggleFilters() {
    this.advancedFilters = !this.advancedFilters;
  }

  rowSelected(row) {
    if (row.expected == 'Unknown Tower' || ((row.lat == null || Number.isNaN(row.lat) || row.lat == 0) && (row.lng == null || Number.isNaN(row.lng) || row.lng == 0))) {
      this.toastService.info('This location is at an unknown tower', 'Unknown Tower', { timeOut: 3000 });
      return;
    }

    const center = new google.maps.LatLng(row.lat, row.long);

    this.map.setCenter(center);
    this.map.setZoom(15);
    this.scrollToMap();
  }

  drawHistoryLocations(locations: any[]) {
    this.hopData = [];
    this.locations = [];
    const locationClusters: any = [];
    if (locations != null) {
      let counter = 0;
      let label = 0;
      for (let i = 0; i < locations.length; i++, counter++, label++) {
        let towerLocationString = 'Unknown Tower';
        if (!(locations[i].Lat == null || locations[i].Long == null) && !locations[i].Tower.UnknownTower) {
          let isExpectedLocation = false;
          let image = '';
          if (this.lat == locations[i].Lat && this.lng == locations[i].Long) {
            isExpectedLocation = true;
            towerLocationString = 'Expected Location';
            image = 'http://maps.google.com/mapfiles/ms/icons/green-dot.png';
          } else {
            towerLocationString = 'Non-Expected Location';
            image = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
          }

          const csq = locations[i].CSQ != null ? locations[i].CSQ : null;
          const radiusDistance = this.detailsObject?.KaDevice?.RingfenceLog?.Radius;
          const distance = this.tools.CalculateRadiusDistanceBasedOnCSQ(csq, radiusDistance);
          const newCluster = {
            label: label + 1,
            counter: 1,
            Lat: locations[i].Lat,
            Long: locations[i].Long,
            expected: isExpectedLocation,
            icon: image,
            tower: locations[i].Tower,
            CSQ: locations[i].CSQ,
            Radius: isExpectedLocation ? radiusDistance : distance,
            Ringfence: locations[i].Radius != null ? locations[i].Radius : null,
            Devices: [this.tools.FormatDateTime(this.tools.ConvertEpochToDate(locations[i].Timestamp))],
          };

          if (this.formGroup.get('DistinctTowerControl')?.value == true) {
            let found = false;
            let foundCluster: any = null;
            for (let j = 0; j < locationClusters.length && !found; j++) {
              if (locationClusters[j].Lat == locations[i].Lat && locationClusters[j].Long == locations[i].Long) {
                found = true;
                foundCluster = locationClusters[j];
              }
            }

            if (!found) {
              // New location cluster encountered
              let nextLabel = 1;

              if (locationClusters != null && locationClusters.length > 0) {
                nextLabel = locationClusters[locationClusters.length - 1].label + 1;
              }
              newCluster.label = nextLabel;
              locationClusters.push(newCluster);
            } else {
              // Add to same cluster
              foundCluster.counter++;
              newCluster.label = foundCluster.label;
              foundCluster.Devices.push(newCluster.Devices[0]);
            }
          } else {
            // handle non clustered data
            locationClusters.push(newCluster);
          }

          locations[i].label = label + 1;
          locations[i].towerlabel = newCluster.label;
          this.locations.push(locations[i]);
        } else {
          // Unkown Tower
          locations[i].label = '?';
          locations[i].towerlabel = '?';
        }

        const obj: any = {
          id: locations[i].ID,
          label: label + 1,
          towerlabel: locations[i].towerlabel,
          timestamp: this.tools.FormatDateTimeFromDate(this.tools.ConvertEpochToDate(locations[i].Timestamp)),
          totimestamp: '',
          cellid: locations[i].Tower != null ? (locations[i].Tower.CellID != null ? locations[i].Tower.CellID : '0') : '',
          lac: locations[i].Tower != null ? (locations[i].Tower.LAC != null ? locations[i].Tower.LAC : '0') : '',
          mcc: locations[i].Tower != null ? locations[i].Tower.MCC : '',
          mnc: locations[i].Tower != null ? this.tools.GetNetworkNameByMNC(locations[i].Tower.MNC) : '',
          rawmnc: locations[i].Tower.MNC,
          lat: locations[i].Lat,
          csq: locations[i].CSQ,
          radius: locations[i].Radius,
          textrange: ((locations[i].CSQ / this.tools.GetMaxCSQ()) * locations[i].Radius).toFixed(2) + ' meter',
          long: locations[i].Long,
          expected: towerLocationString,
          concat: null,
          hopCount: locations[i].HopCount,
        };

        if (this.detailsObject.KaDevice != null) {
          if (i == 0) {
            obj['totimestamp'] = this.tools.FormatDateTime(this.tools.ParseAPIDateWithTimezone(this.detailsObject.KaDevice.LastComms));
          } else {
            obj['totimestamp'] = this.hopData[i - 1].timestamp;
          }
        }

        obj.concat = (obj.id + obj.label + obj.timestamp + obj.cellid + obj.lac + obj.mcc + obj.mnc + obj.expected).toLowerCase();

        this.hopData.push(obj);
      }
    } else {
      // No Location History to draw
    }

    this.locationClusters = locationClusters;
    this.hopsOffset = 0;
    this.filteredHopData = this.hopData.slice(this.hopsOffset, 10);
    this.loadingHops = false;
    this.formGroup.enable();

    this.initiateRedraw();
  }

  initiateRedraw() {
    setTimeout(() => {
      this.clearMarkers();
      this.clearCircles();
      this.clearSignalStrengths();
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers();
      }
      if (this.locations == null) {
        this.locations = [];
        this.locationClusters = [];
      }

      this.initialiseMap(() => {
        setTimeout(() => {
          this.drawMovementMarkers(this.locationClusters);
        });
      }, this.locationClusters != null && this.locationClusters.length > 0);
    });
  }

  paginateHops(eve: any) {
    this.currentHopsPage = eve;
    this.loadingHops = true;
    this.hopsOffset = (eve - 1) * 10;
    if (this.hopsSearch != '') {
      this.filteredHopData = this._filter(this.hopsSearch);
    } else {
      this.filteredHopData = this.hopData?.slice(this.hopsOffset, this.hopsOffset + 10) ? this.hopData.slice(this.hopsOffset, this.hopsOffset + 10) : [];
    }
    setTimeout(() => {
      this.loadingHops = false;
    });
  }

  toggleHops() {
    this.showHops = !this.showHops;
  }

  getDeviceTowerPingList(pings: string[]): string {
    let out = `<div>`;

    for (let i = 0; i < pings.length; i++) {
      out += `<p>${i + 1}: ${pings[i]}</p>`;
    }

    out += `</div>`;

    return out;
  }

  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    const fuzzy: any = go(filterValue, this.hopData, { allowTypo: false, key: 'concat', threshold: -100000 });
    const newArr: any = [];
    fuzzy.forEach((element) => {
      newArr.push(element.obj);
    });
    return newArr.splice(this.hopsOffset, 10);
  }

  submitHopSearch(eve) {
    this.loadingHops = true;

    if (eve == null || eve == '') {
      this.hopsFiltersCleared();
    } else {
      this.hopsSearch = eve;
      this.currentHopsPage = this.hopsOffset / 10;
      this.filteredHopData = this._filter(eve);
    }

    setTimeout(() => {
      this.loadingHops = false;
    });
  }

  zoomToMarker(center: google.maps.LatLng, zoom: number) {
    this.map.setCenter(center);
    this.map.setZoom(zoom);
  }

  drawLocationTraceCircle(center: google.maps.LatLng, mnc: number, csq: number, radius: number, range: number, drawRadius = true, drawSignalStrength = true) {
    if (radius == null) {
      radius = this.detailsObject?.KaDevice?.RingfenceLog?.Radius;
    }

    if (Number.isNaN(center.lat()) || Number.isNaN(center.lng())) {
      return;
    }

    if (drawRadius) {
      const ringfenceRadius = new google.maps.Circle({
        strokeColor: this.ringfenceGreenColor,
        strokeOpacity: this.radiusCircleConfig['strokeOpacity'],
        strokeWeight: this.radiusCircleConfig['strokeWeight'],
        fillColor: this.ringfenceGreenColor,
        fillOpacity: this.radiusCircleConfig['fillOpacity'],
        map: this.map,
        center: center,
        radius: radius,
      });
      this.ringfenceCircles.push(ringfenceRadius);
    }

    if (drawSignalStrength) {
      const signalStrength = this.tools.CalculateRadiusDistanceBasedOnCSQ(csq, radius);

      const signalStrengthRadius = new google.maps.Circle({
        strokeColor: this.signalStrengthColor,
        strokeOpacity: this.signalCircleConfig['strokeOpacity'],
        strokeWeight: this.signalCircleConfig['strokeWeight'],
        fillColor: this.signalStrengthColor,
        fillOpacity: this.signalCircleConfig['fillOpacity'],
        map: this.map,
        center: center,
        radius: signalStrength,
      });
      this.signalStrengths.push(signalStrengthRadius);
    }
  }

  PreviousLocationTrace() {
    ++this.CurrentTraceIndex;
    this.GoToLocationTrace(this.hopData[this.CurrentTraceIndex]);
  }

  NextLocationTrace() {
    --this.CurrentTraceIndex;
    this.GoToLocationTrace(this.hopData[this.CurrentTraceIndex]);
  }

  GoToLocationTrace(hopInfo: any) {
    this.clearCircles();
    if (this.hopData[this.CurrentTraceIndex] == null) {
      this.toastService.info('There is no more location data to view.', 'Backtrace ended', { timeOut: 8000 });
      return;
    }

    if (
      this.hopData[this.CurrentTraceIndex].expected == 'Unknown Tower' ||
      !(
        this.hopData[this.CurrentTraceIndex].lat != null &&
        this.hopData[this.CurrentTraceIndex].long != null &&
        this.hopData[this.CurrentTraceIndex].lat != 0 &&
        this.hopData[this.CurrentTraceIndex].long != 0
      )
    ) {
      this.toastService.info('The current hop was at an unknown tower.', 'Unknown Tower', { timeOut: 8000 });
      return;
    }
    const center = new google.maps.LatLng(hopInfo.lat, hopInfo.long);
    this.zoomToMarker(center, 15);
  }

  toggleAllSignalStrength(toggle: boolean) {
    this.clearSignalStrengths();
    this.locationClusters.forEach((element) => {
      if (element.Lat != null && element.Long != null && element.Lat != 0 && element.Long != 0) {
        const center = new google.maps.LatLng(element.Lat, element.Long);
        if (element.expected == true) {
          // Draw Expected Locations Signal Strength
          const radius = this.tools.CalculateRadiusDistanceBasedOnCSQ(element.CSQ, element.Radius);
          this.drawCircle(this.signalStrengths, this.signalStrengthColor, center, radius, this.signalCircleConfig);
        } else {
          this.drawLocationTraceCircle(center, element.tower.MNC, element.CSQ, element.Radius, element.tower.Range, false, toggle);
        }
      }
    });
  }

  drawCircle(outArray: any[], color: string, center: google.maps.LatLng, radius: number, template: any) {
    const circle = new google.maps.Circle({
      strokeColor: color,
      strokeOpacity: 0,
      strokeWeight: 0,
      fillColor: color,
      fillOpacity: 0,
      map: this.map,
      center: center,
      radius: radius,
    });

    if (template) {
      circle.set('strokeOpacity', template['strokeOpacity']);
      circle.set('strokeWeight', template['strokeWeight']);
      circle.set('fillOpacity', template['fillOpacity']);
    }

    outArray.push(circle);
  }

  toggleAllRingfence(toggle: boolean) {
    this.clearRingfenceCircles();
    this.locationClusters.forEach((element) => {
      if (element.Lat != null && element.Long != null && element.Lat != 0 && element.Long != 0) {
        const center = new google.maps.LatLng(element.Lat, element.Long);
        if (element.expected == true) {
          // Draw Expected Locations Ringfence
          this.drawCircle(this.ringfenceCircles, this.ringfenceGreenColor, center, element.Radius, this.radiusCircleConfig);
        } else if (element.Ringfence != null) {
          // Draw normal ringfence if necessary
          this.drawLocationTraceCircle(center, element.tower.MNC, element.CSQ, element.Ringfence, element.tower.Range, toggle, false);
        }
      }
    });
  }

  setCluster() {
    const styles = [
      MarkerClusterer.withDefaultStyle({
        width: 30,
        height: 30,
        url: '../assets/icon/marker-pinlet-30.png',
        textColor: '#ffffff',
        textSize: 10,
      }),
      MarkerClusterer.withDefaultStyle({
        width: 45,
        height: 45,
        url: '../assets/icon/marker-pinlet-45.png',
        textColor: '#ffffff',
        textSize: 11,
      }),
      MarkerClusterer.withDefaultStyle({
        width: 60,
        height: 60,
        url: '../assets/icon/marker-pinlet-60.png',
        textColor: '#ffffff',
        textSize: 12,
      }),
      MarkerClusterer.withDefaultStyle({
        width: 75,
        height: 75,
        url: '../assets/icon/marker-pinlet-75.png',
        textColor: '#ffffff',
        textSize: 13,
      }),
    ];

    this.markerClusterer = new MarkerClusterer(this.map, this.markers, {
      maxZoom: 15,
      gridSize: 25,
      styles: styles,
      clusterClass: undefined,
    });
  }

  toggleAdvancedLocationHistory() {
    this.advancedLocation = !this.advancedLocation;
    this.advancedToggled.emit(this.advancedLocation.valueOf());
  }

  hopsFiltersCleared() {
    this.hopsSearch = '';
    this.hopsOffset = 0;
    this.currentHopsPage = 1;
    this.filteredHopData = this.hopData.slice(0, 10);
  }

  hopSortToggled() {
    this.hopsSearch = '';
    this.hopsOffset = 0;
    this.currentHopsPage = 1;
    this.hopData = this.hopData.sort((a, b) => {
      if (this.hopsDesc) {
        return a.label - b.label;
      } else {
        return b.label - a.label;
      }
    });
    this.hopsDesc = !this.hopsDesc;
    this.filteredHopData = this.hopData.slice(0, 10);
  }

  scrollToMap() {
    const element: any = document.querySelector('#map');
    element.scrollIntoView();
  }

  toggleLocationTracing() {
    this.LocationTracingEnabled = !this.LocationTracingEnabled;
    this.advancedFilters = !this.LocationTracingEnabled;

    this.clearCircles();
    this.scrollToMap();
    if (this.LocationTracingEnabled) {
      if (this.hopData && this.hopData.length > 0) {
        this.CurrentTraceIndex = 0;
        this.TotalTraceIndex = this.hopData.length;

        if (this.hopData[0].expected == 'Unknown Tower') {
          this.toastService.info('The last known hop was at an unknown tower.', 'Unknown Tower', { timeOut: 8000 });
          return;
        }

        const center = new google.maps.LatLng(this.hopData[0].lat, this.hopData[0].long);
        this.drawLocationTraceCircle(
          center,
          this.hopData[0].rawmnc,
          this.hopData[0].csq,
          this.hopData[0].radius,
          this.hopData[0].range,
          this.showRingfences,
          this.showSignalStrengths,
        );
        this.zoomToMarker(center, 15);
      }
    } else {
      this.CurrentTraceIndex = -1;
      this.TotalTraceIndex = -1;
      this.initiateRedraw();
    }
  }

  searchLocation() {
    const params = new GetDeviceLocationRequest();

    if (this.formGroup.valid) {
      if (this.isMikrotik) {
        params.StartTime = this.tools.ConvertDateToEpoch(this.tempStart);
        params.EndTime = this.tools.ConvertDateToEpoch(this.tempEnd);
        params.Count = this.formGroup.get('MaxHopControl')?.value;
        params.LAC = this.formGroup.get('LACSearchControl')?.value;
        params.CellID = this.formGroup.get('CellIDSearchControl')?.value;
        params.Search = this.formGroup.get('FuzzySearchControl')?.value;
        params.Operator = this.formGroup.get('OperatorControl')?.value;
      } else {
        params.StartTime = this.tools.ConvertDateToEpoch(this.tempStart);
        params.EndTime = this.tools.ConvertDateToEpoch(this.tempEnd);
        params.Count = this.formGroup.get('MaxHopControl')?.value;
        params.LAC = this.formGroup.get('LACSearchControl')?.value;
        params.MNC = this.formGroup.get('MNCSearchControl')?.value;
        params.CellID = this.formGroup.get('CellIDSearchControl')?.value;
        params.MCC = this.formGroup.get('MCCSearchControl')?.value;
        params.DistinctTowers = this.formGroup.get('DistinctTowerControl')?.value;
      }

      this.searchChanged.emit(params);
    }
  }
}
